import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DataService } from '../../../../../../services/dashboard/data.service';
import { DmtUtilityService } from '../../../../../../services/dashboard/dmt-utility.service';
import { DataPoint, NtChartRegionInterface } from '../../../../../../shared/interfaces/nt-chart-region.interface';

interface ForecastAccuracyRow {
  mese: string;
  forecast: number | null;
  actual: number | null;
  delta: number | null;
}

@Component({
  selector: 'app-forecast-accuracy-open-to-market',
  imports: [CommonModule],
  templateUrl: './forecast-accuracy-open-to-market.component.html',
  styleUrl: './forecast-accuracy-open-to-market.component.css'
})
export class ForecastAccuracyOpenToMarketComponent implements OnInit {
  isLoading: boolean = true;
  hasData: boolean = false;

  public medicineId: number = 0;
  public countryId: number = 1;

  rows: ForecastAccuracyRow[] = [];

  constructor(private us: DmtUtilityService, private dataService: DataService) { }

  ngOnInit(): void {
    this.getSessionVariables();
    this.dataService.getOpenToMarketChartInfo(this.medicineId, this.countryId).subscribe((infoData: NtChartRegionInterface) => {
      this.hasData =
        !!infoData?.labels?.length &&
        (infoData.actual.some((val: any) => val !== null) ||
          infoData.target.some((val: any) => val !== null));

      if (!this.hasData) {
        this.rows = this.createPlaceholderData();
        this.isLoading = false;
        return;
      }

      this.rows = this.buildRows(infoData);
      this.isLoading = false;
    });
  }

  getSessionVariables() {
    this.medicineId = this.us.getSessionVariableNumber('medicineId', 0);
    this.countryId = this.us.getSessionVariableNumber('countryId');
  }

  /**
   * Placeholder rows shown when the endpoint returns no usable data
   */
  private createPlaceholderData(): ForecastAccuracyRow[] {
    return Array.from({ length: 4 }, (_, index) => ({
      mese: `Mese ${index + 1}`,
      forecast: null,
      actual: null,
      delta: null
    }));
  }

  // --- Cumulative logic cloned from open-to-market-curve.component.ts, so table values match the chart exactly ---

  private toYm(input: string | Date): string {
    if (input instanceof Date && !isNaN(input.getTime())) {
      const y = input.getFullYear();
      const m = String(input.getMonth() + 1).padStart(2, '0');
      return `${y}-${m}`;
    }
    const raw = String(input).trim();
    const m = raw.match(/^(\d{4})-?(\d{2})(?:-?\d{2})?$/);
    if (!m) throw new Error(`Bad month format: ${raw}`);
    return `${m[1]}-${m[2]}`;
  }

  private ymToNum(ym: string): number {
    const [y, m] = ym.split('-').map(Number);
    return y * 100 + m;
  }

  private sortLabelsChrono(labels: string[]): string[] {
    return [...labels]
      .map((l) => this.toYm(l))
      .sort((a, b) => this.ymToNum(a) - this.ymToNum(b));
  }

  private accumulate(points: DataPoint[]): Map<string, number> {
    const sums = new Map<string, number>();
    for (const p of points || []) {
      const k = this.toYm(p.month);
      const roundedValue = Math.round(Number(p.value) * 100) / 100;
      sums.set(k, (sums.get(k) ?? 0) + roundedValue);
    }
    const months = [...sums.keys()].sort((a, b) => this.ymToNum(a) - this.ymToNum(b));
    const acc = new Map<string, number>();
    let running = 0;
    for (const m of months) {
      running += sums.get(m)!;
      running = Math.round(running * 100) / 100;
      acc.set(m, running);
    }
    return acc;
  }

  private firstMonthNum(acc: Map<string, number>): number | null {
    if (acc.size === 0) return null;
    return Math.min(...[...acc.keys()].map((k) => this.ymToNum(k)));
  }

  private buildSeries(
    labels: string[],
    acc: Map<string, number>,
    carryForward: boolean,
    maxMonthNum?: number,
    stopAt100?: boolean,
  ): (number | null)[] {
    const startNum = this.firstMonthNum(acc);
    const out: (number | null)[] = [];
    let last: number | undefined;
    let reachedMax = false;

    for (const label of labels) {
      const num = this.ymToNum(label);
      if (startNum == null || num < startNum) {
        out.push(null);
        continue;
      }
      if (maxMonthNum != null && num > maxMonthNum) {
        out.push(null);
        continue;
      }
      if (stopAt100 && reachedMax) {
        out.push(null);
        continue;
      }

      const v = acc.get(label);
      if (v != null) {
        out.push(v);
        last = v;
        if (stopAt100 && v >= 100) reachedMax = true;
      } else if (carryForward && last != null) {
        out.push(last);
        if (stopAt100 && last >= 100) reachedMax = true;
      } else {
        out.push(null);
      }
    }
    return out;
  }

  private buildRows(infoData: NtChartRegionInterface): ForecastAccuracyRow[] {
    const labelsRaw: string[] = infoData.labels || [];
    const labels = this.sortLabelsChrono(labelsRaw);

    const targetAcc = this.accumulate(infoData.target || []);
    const actualAcc = this.accumulate(infoData.actual || []);

    const now = new Date();
    const currentNum = this.ymToNum(
      `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
    );

    const forecastValues = this.buildSeries(labels, targetAcc, true);
    const actualValues = this.buildSeries(labels, actualAcc, true, currentNum, true);

    // Sequential "Mese N" counter follows position in the deduped/sorted label list,
    // so skipped calendar months don't create gaps in the numbering.
    return labels.map((_, index) => {
      const forecast = forecastValues[index];
      const actual = actualValues[index];
      const delta =
        forecast != null && actual != null
          ? Math.round((actual - forecast) * 100) / 100
          : null;

      return {
        mese: `Mese ${index + 1}`,
        forecast,
        actual,
        delta,
      };
    });
  }

getDeltaClass(row: ForecastAccuracyRow): string {
  const { delta, forecast, actual } = row;

  if (delta == null) return '';

  // exact 0/0 case: no background
  if (forecast === 0 && actual === 0) return '';

  const absDelta = Math.abs(delta);

  if (absDelta <= 1) {
    return 'bg-green-500';
  }

  if (absDelta <= 2) {
    return 'bg-green-200';
  }

  if (absDelta <= 3) {
    return 'bg-delta-light';
  }

  return '';
}

}
